import { InternalCategoryFeature } from '../../../../types/InternalCategory'
import { InternalTag, InternalTagTargets, InternalTagType } from '../../../../types/InternalTag'
import { isAssignableTagType } from './tagHelpers'

export const assignTagTypes = [InternalTagType.Feature, InternalTagType.Game]

export const getAssignableTags = (tags: InternalTag[], type: InternalTagType) =>
  tags.filter((tag) => tag.type === type).sort((a, b) => a.name.localeCompare(b.name))

// A tag in the URL decides the type; otherwise the first tag of the type by name is used.
export const resolveAssignTag = (tags: InternalTag[], typeParam: string | null, tagIdParam: string | null) => {
  const requested = tags.find((tag) => tag.id === tagIdParam && isAssignableTagType(tag.type))
  if (requested) return { type: requested.type, tag: requested }
  const type = assignTagTypes.find((assignType) => assignType === typeParam) ?? InternalTagType.Feature
  return { type, tag: getAssignableTags(tags, type)[0] as InternalTag | undefined }
}

export type TagTargetChange = { added: number[]; removed: number[] }

// Per tag id, in toggle order.
export type TagTargetChanges = Record<string, TagTargetChange>

export const getEffectiveTargets = (tag: InternalTag, change?: TagTargetChange) => {
  const stored = tag.targets ?? []
  if (!change) return stored
  return [...stored.filter((id) => !change.removed.includes(id)), ...change.added.filter((id) => !stored.includes(id))]
}

export const toggleTarget = (changes: TagTargetChanges, tag: InternalTag, id: number): TagTargetChanges => {
  const change = changes[tag.id] ?? { added: [], removed: [] }
  const isSelected = getEffectiveTargets(tag, change).includes(id)
  const without = (ids: number[]) => ids.filter((value) => value !== id)
  const next: TagTargetChange = isSelected
    ? change.added.includes(id)
      ? { ...change, added: without(change.added) }
      : { ...change, removed: [...change.removed, id] }
    : change.removed.includes(id)
    ? { ...change, removed: without(change.removed) }
    : { ...change, added: [...change.added, id] }

  if (next.added.length || next.removed.length) return { ...changes, [tag.id]: next }
  const { [tag.id]: _cancelled, ...rest } = changes
  return rest
}

export type TagTargetRequest = { type: InternalTagType; action: 'add' | 'remove'; targets: InternalTagTargets }

// Legacy's order: per type (feature, then game) the additions, then the removals.
export const toTargetRequests = (changes: TagTargetChanges, tags: InternalTag[]): TagTargetRequest[] => {
  const typesById = new Map(tags.map((tag) => [tag.id, tag.type]))
  return assignTagTypes.flatMap((type) =>
    (['add', 'remove'] as const).flatMap((action) => {
      const targets: InternalTagTargets = {}
      Object.entries(changes).forEach(([tagId, change]) => {
        const ids = action === 'add' ? change.added : change.removed
        if (typesById.get(tagId) === type && ids.length) targets[tagId] = ids
      })
      return Object.keys(targets).length ? [{ type, action, targets }] : []
    })
  )
}

// Keeps what the stored tags don't reflect yet; a tag that no longer exists drops its changes but is still reported.
export const checkStoredChanges = (changes: TagTargetChanges, storedTags: InternalTag[]) => {
  const storedById = new Map(storedTags.map((tag) => [tag.id, tag]))
  const remaining: TagTargetChanges = {}
  const failedTagIds: string[] = []
  Object.entries(changes).forEach(([tagId, change]) => {
    const stored = storedById.get(tagId)
    if (!stored) {
      failedTagIds.push(tagId)
      return
    }
    const targets = stored.targets ?? []
    const next = { added: change.added.filter((id) => !targets.includes(id)), removed: change.removed.filter((id) => targets.includes(id)) }
    if (next.added.length || next.removed.length) {
      remaining[tagId] = next
      failedTagIds.push(tagId)
    }
  })
  return { remaining, failedTagIds }
}

export type FeatureRow = { feature: InternalCategoryFeature; number: number }

export const buildFeatureRows = (features: InternalCategoryFeature[], activeOnly: boolean, search: string): FeatureRow[] => {
  const query = search.trim().toLowerCase()
  return [...features]
    .sort((a, b) => a.ordr - b.ordr)
    .filter((feature) => (!activeOnly || feature.active) && (!query || (feature.name ?? '').toLowerCase().includes(query)))
    .map((feature, index) => ({ feature, number: index + 1 }))
}

type Named = { id: string; name: string }

export const getPositionCount = (feature: InternalCategoryFeature) => {
  const stores = feature.stores ?? []
  const markets = feature.markets ?? []
  return stores.length && markets.length ? `${stores.length}, ${markets.length}` : '---'
}

export const getPositionNames = (feature: InternalCategoryFeature, stores: Named[] = [], markets: Named[] = []) => {
  const nameOf = (items: Named[], id: string) => items.find((item) => item.id === id)?.name ?? id
  const marketIds = feature.markets ?? []
  return (feature.stores ?? []).flatMap((storeId) => marketIds.map((marketId) => `${nameOf(stores, storeId)} ${nameOf(markets, marketId)}`))
}
